import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Login from './pages/Login';
import Register from './pages/Register';
import AdminLogin from './pages/AdminLogin';
import ForgotPassword from './pages/ForgotPassword';
import EmployeeDashboard from './pages/EmployeeDashboard';
import AdminDashboard from './pages/AdminDashboard';
import LeavesPage from './pages/LeavesPage';
import HolidayTracker from './pages/HolidayTracker';
import ErrorBoundary from './components/ErrorBoundary';
import { reportError, persistErrorLocally } from './lib/errorReport';
import { Toaster } from './components/ui/sonner';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onWindowError = (event) => {
      const err = event?.error || new Error(event?.message || 'Uncaught error');
      persistErrorLocally(err);
      reportError({ message: err.message, stack: err.stack });
    };
    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      const err = reason instanceof Error ? reason : new Error(String(reason || 'Unhandled rejection'));
      persistErrorLocally(err);
      reportError({ message: err.message, stack: err.stack });
    };
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      if (token && storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed && parsed.user_id && parsed.role) {
          setUser(parsed);
        } else {
          // Corrupted user data - clear it
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
    } catch (e) {
      // JSON parse failed - clear corrupted data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg font-medium">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} />} />
            <Route path="/register" element={!user ? <Register onLogin={handleLogin} /> : <Navigate to="/dashboard" />} />
            <Route path="/admin-login" element={!user ? <AdminLogin onLogin={handleLogin} /> : <Navigate to="/admin" />} />
            <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
            
            <Route
              path="/dashboard"
              element={user && user.role === 'employee' ? <EmployeeDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/leaves"
              element={user && user.role === 'employee' ? <LeavesPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/holiday-tracker"
              element={user && user.role === 'employee' ? <HolidayTracker user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            
            <Route
              path="/admin"
              element={user && user.role === 'admin' ? <AdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/admin-login" />}
            />
            
            <Route path="/" element={<Navigate to={user ? (user.role === 'admin' ? '/admin' : '/dashboard') : '/login'} />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
      <Toaster />
    </>
  );
}

export default App;